import React from "react";

const AboutPage = () => {
  return (
    <div className="w-full font-sans overflow-x-hidden">
      
      {/* ===== PAGE CSS - PREMIUM ANIMATIONS ===== */}
      <style>
        {`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        
        @keyframes pulse-soft {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-pulse-soft {
          animation: pulse-soft 3s ease-in-out infinite;
        }
        
        .animate-slide-up {
          animation: slideUp 0.8s cubic-bezier(0.2, 0.9, 0.3, 1) forwards;
        }
        
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        `}
      </style>

      {/* ===== HERO SECTION - CLEAN, POWERFUL ===== */}
      <section className="relative h-[450px] md:h-[420px] sm:h-[380px] overflow-hidden bg-gradient-to-br from-teal-600 to-emerald-600">
        
        {/* Animated orbs - subtle */}
        <div className="absolute top-20 -left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 -right-20 w-80 h-80 bg-amber-300/20 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Content - centered */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 max-w-5xl mx-auto text-center">
          <span className="inline-block px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6 border border-white/30 animate-slide-up">
            About DonatePe
          </span>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-slide-up delay-100">
            Fundraising, 
            <br />
            <span className="relative inline-block mt-2">
              <span className="relative z-10">reimagined.</span>
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-white/30 blur-md"></span>
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto leading-relaxed animate-slide-up delay-200">
            Simple. Transparent. Free. Built for the causes you believe in.
          </p>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* ===== MISSION STATEMENT - CLEAN, BOLD ===== */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        
        <div className="text-center max-w-4xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-full border border-teal-100 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
            </span>
            <span className="text-xs font-medium text-teal-700 uppercase tracking-wider">
              Our promise
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-8">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
              100% of every rupee
            </span>
            <br />
            <span className="text-gray-800">goes to the cause.</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            No platform fees. No hidden charges. No fine print. Just a better way to raise money for what matters.
          </p>
        </div>

        {/* ===== CORE VALUES - THREE PILLARS ===== */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12 mb-20">
          
          {/* Pillar 1 - Zero fees */}
          <div className="group relative text-center animate-slide-up">
            <div className="absolute -inset-3 bg-gradient-to-r from-teal-100/50 to-emerald-100/50 rounded-3xl blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
            
            <div className="relative bg-white rounded-3xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500 group-hover:-translate-y-2">
              
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center mx-auto shadow-md">
                  <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Zero platform fees
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                Every rupee raised goes directly to your cause. We don't take a cut. Period.
              </p>
            </div>
          </div>

          {/* Pillar 2 - Give back */}
          <div className="group relative text-center animate-slide-up delay-100">
            <div className="absolute -inset-3 bg-gradient-to-r from-amber-100/50 to-orange-100/50 rounded-3xl blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
            
            <div className="relative bg-white rounded-3xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500 group-hover:-translate-y-2">
              
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center mx-auto shadow-md">
                  <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Monthly giving back
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                We support active campaigns every month. Your success is our success.
              </p>
            </div>
          </div>

          {/* Pillar 3 - Global reach */}
          <div className="group relative text-center animate-slide-up delay-200">
            <div className="absolute -inset-3 bg-gradient-to-r from-blue-100/50 to-indigo-100/50 rounded-3xl blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
            
            <div className="relative bg-white rounded-3xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500 group-hover:-translate-y-2">
              
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
                <div className="relative w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto shadow-md">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Global community
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                Raise funds from anywhere. Connect with donors worldwide.
              </p>
            </div>
          </div>
        </div>

        {/* ===== FEATURE HIGHLIGHTS ===== */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 md:p-12 lg:p-16 mb-20 border border-gray-100">
          
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
            
            {/* Left - Key features */}
            <div className="space-y-8">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                Why donors choose{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                  DonatePe
                </span>
              </h3>
              
              <div className="space-y-5">
                {[
                  'Quick 2-minute setup',
                  'Recurring donations supported',
                  'Real-time campaign tracking',
                  'Secure payments',
                  'Instant withdrawals',
                  '24/7 customer support'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-lg text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right - Visual */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-teal-100/50 to-emerald-100/50 rounded-3xl blur-2xl"></div>
              
              <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white text-xl">
                    💝
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Transparent by design</p>
                    <p className="text-sm text-gray-500">See exactly where every rupee goes</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Campaign goal</span>
                    <span className="font-semibold text-gray-900">₹100,000</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Raised so far</span>
                    <span className="font-semibold text-teal-600">₹75,000</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full mt-2">
                    <div className="w-3/4 h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 pt-2">
                    <span>0% platform fee</span>
                    <span>You keep 100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== COMMUNITY SECTION ===== */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="relative inline-flex flex-col items-center">
            
            <div className="flex items-center justify-center mb-8">
              <div className="flex -space-x-3">
                {[1,2,3,4,5].map((i) => (
                  <div
                    key={i}
                    className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 border-3 border-white shadow-xl flex items-center justify-center text-white text-lg font-bold"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
                <div className="w-12 h-12 rounded-full bg-white border-3 border-teal-200 shadow-xl flex items-center justify-center text-sm font-semibold text-teal-600">
                  +you
                </div>
              </div>
            </div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Join 50,000+ campaigners
            </h3>
            
            <p className="text-xl text-gray-600 mb-10 max-w-2xl">
              Start your fundraiser today. No fees. No deadlines. No pressure.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/start-fundraiser"
                className="group relative px-10 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl hover:shadow-teal-200/50 transition-all duration-500 hover:scale-105"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Start a fundraiser
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-xl opacity-50 group-hover:opacity-80 transition-opacity duration-500"></div>
              </a>
              
              <a
                href="/about"
                className="px-10 py-5 bg-white border-2 border-gray-200 text-gray-700 rounded-full font-semibold text-lg hover:border-teal-600 hover:text-teal-600 transition-all duration-300"
              >
                Learn more
              </a>
            </div>
            
            <div className="flex items-center justify-center gap-3 mt-10">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></span>
              <p className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">No platform fees.</span> You keep 100% of what you raise.
              </p>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;